import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { axiosCaller } from '../../utils/apiCaller';
import { formatApiErrorMessage } from '../../utils/apiError';
import { toast } from 'react-toastify';
import { t } from '../../i18n';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import '../../components/Admin/adminShared.scss';
import './CreateUserForm.scss';

interface Permission { value: number; label: string; keyLabel: string; module: string | null; action: string | null; }
interface Responsibility { id: number; label: string; }
interface Department { value: number; label: string; }

type TabKey = 'profile' | 'permissions' | 'activity';

// ── Module icons (16x16 SVGs) ──
const MI = ({ d }: { d: string }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);

const MODULE_ICONS: Record<string, React.ReactNode> = {
    contact: <MI d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100 8 4 4 0 000-8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />,
    measures: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4h4v16H2zM14 4h4v16h-4z" /><line x1="6" y1="12" x2="3" y2="12" /><line x1="18" y1="12" x2="15" y2="12" /></svg>,
    offers: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>,
    agreements: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
    production: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>,
    warehouse: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>,
    transport: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>,
    installation: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" /></svg>,
    service: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" /></svg>,
    impression: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" /></svg>,
    fullPrivileges: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
};

// Module-key → i18n-key map. Render via <FormattedMessage id={MODULE_LABEL_KEYS[moduleKey]}/>.
const MODULE_LABEL_KEYS: Record<string, string> = {
    contact: 'admin.permissions.module.contact',
    measures: 'admin.permissions.module.measures',
    offers: 'admin.permissions.module.offers',
    agreements: 'admin.permissions.module.agreements',
    production: 'admin.permissions.module.production',
    warehouse: 'admin.permissions.module.warehouse',
    transport: 'admin.permissions.module.transport',
    installation: 'admin.permissions.module.installation',
    service: 'admin.permissions.module.service',
    impression: 'admin.permissions.module.impression',
};

// Static action columns. Labels rendered via <FormattedMessage id={a.i18nKey}/> at call-site.
const ACTION_COLUMNS: { key: string; i18nKey: string }[] = [
    { key: 'read',   i18nKey: 'admin.permissions.action.read' },
    { key: 'create', i18nKey: 'admin.permissions.action.create' },
    { key: 'edit',   i18nKey: 'admin.permissions.action.edit' },
    { key: 'export', i18nKey: 'admin.permissions.action.export' },
];

const PERMISSION_GROUPS: { i18nKey: string; keys: string[] }[] = [
    { i18nKey: 'admin.permissions.group.sales',      keys: ['contact', 'measures', 'offers', 'agreements'] },
    { i18nKey: 'admin.permissions.group.production', keys: ['production', 'warehouse', 'transport', 'installation', 'service'] },
    { i18nKey: 'admin.permissions.group.other',      keys: ['impression'] },
];

const TABS: { key: TabKey; i18nKey: string; icon: React.ReactNode; editOnly?: boolean }[] = [
    {
        key: 'profile', i18nKey: 'admin.users.tab.profile',
        icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
    },
    {
        key: 'permissions', i18nKey: 'admin.users.tab.permissions',
        icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
    },
    {
        key: 'activity', i18nKey: 'admin.users.tab.activity',
        icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
        editOnly: true,
    },
];

// ── Collapsible card (extracted to avoid re-mount on parent re-render) ──
const Card = ({ id, title, hint, danger, children, noPadding, collapsed, setCollapsed }: {
    id: string; title: React.ReactNode; hint?: React.ReactNode; danger?: boolean; children: React.ReactNode; noPadding?: boolean;
    collapsed: Record<string, boolean>; setCollapsed: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}) => {
    const isCollapsed = collapsed[id] || false;
    const toggle = () => setCollapsed(prev => ({ ...prev, [id]: !isCollapsed }));
    return (
        <div className={`admin-card ${danger ? 'cuf-danger-card' : ''}`}>
            <div className={`cuf-card-header ${danger ? '' : 'cuf-card-header-filled'}`} onClick={toggle} style={{ cursor: 'pointer' }}>
                <h3>{title}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {hint && <span className="cuf-card-hint">{hint}</span>}
                    <svg className={`cuf-card-chevron ${isCollapsed ? '' : 'open'}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                </div>
            </div>
            <div className={`cuf-card-collapse ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                {noPadding ? children : <div className="cuf-card-body">{children}</div>}
            </div>
        </div>
    );
};

const CreateUserForm: React.FC<{ history: any }> = ({ history }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });
    const { userId } = useParams<{ userId?: string }>();
    const isEdit = !!userId;
    const currentUserId = Number(localStorage.getItem('mikaUserId'));

    const [activeTab, setActiveTab] = useState<TabKey>('profile');
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [avatar, setAvatar] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const [createdAt, setCreatedAt] = useState<string | null>(null);

    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
    const [selectedResponsibilities, setSelectedResponsibilities] = useState<number[]>([]);
    const [selectedAdminDepts, setSelectedAdminDepts] = useState<number[]>([]);

    const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
    const [allResponsibilities, setAllResponsibilities] = useState<Responsibility[]>([]);
    const [allDepartments, setAllDepartments] = useState<Department[]>([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deactivateConfirm, setDeactivateConfirm] = useState(false);
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

    const hasProductionPermission = allPermissions.some(
        p => p.module === 'production' && selectedPermissions.includes(p.value)
    );
    const visibleTabs = useMemo(() => TABS.filter(tb => !tb.editOnly || isEdit), [isEdit]);

    useEffect(() => {
        Promise.all([
            axiosCaller('GET', 'admin/permissions'),
            axiosCaller('GET', 'responsibilities'),
            axiosCaller('GET', 'all-departments'),
        ]).then(([permRes, respRes, deptRes]) => {
            setAllPermissions(permRes?.data?.data || []);
            setAllResponsibilities((respRes?.data?.data || []).map((r: any) => ({ id: r.id || r.value, label: r.label || r.name })));
            setAllDepartments((deptRes?.data?.data || []).map((d: any) => ({ value: d.value || d.id, label: d.label || d.name })));
            if (userId) {
                axiosCaller('GET', `admin/users/${userId}`).then(res => {
                    const u = res?.data?.data;
                    if (u) {
                        setFirstname(u.firstname || ''); setLastname(u.lastname || '');
                        setEmail(u.email || ''); setUsername(u.username || '');
                        setAvatarPreview(u.avatar || null);
                        setCreatedAt(u.createdAt || u.created_at || null);
                        setSelectedPermissions((u.permissions || []).map((p: any) => p.value));
                        setSelectedResponsibilities((u.responsibilities || []).map((r: any) => r.id));
                        setSelectedAdminDepts(u.adminDepartments || []);
                    }
                    setLoading(false);
                });
            } else { setLoading(false); }
        });
    }, [userId]);

    const toggleItem = (list: number[], setList: (v: number[]) => void, id: number) => {
        setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
    };

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) { setAvatar(file); setAvatarPreview(URL.createObjectURL(file)); }
    };

    const handleSave = async () => {
        if (!firstname.trim() || !lastname.trim() || !username.trim()) {
            toast.error(t('admin.users.toast.fillRequired')); return;
        }
        if (!isEdit && !password) {
            toast.error(t('admin.users.toast.passwordRequired')); return;
        }
        if (password && password !== passwordConfirmation) {
            toast.error(t('admin.users.toast.passwordMismatch')); return;
        }
        if (selectedPermissions.length === 0) {
            toast.error(t('admin.users.toast.permissionsRequired')); return;
        }
        setSaving(true);
        const payload = new FormData();
        payload.append('firstname', firstname); payload.append('lastname', lastname);
        payload.append('email', email); payload.append('username', username);
        if (password) { payload.append('password', password); payload.append('passwordConfirmation', passwordConfirmation); }
        selectedPermissions.forEach(p => payload.append('permissionsId[]', String(p)));
        selectedResponsibilities.forEach(r => payload.append('responsibilityId[]', String(r)));
        selectedAdminDepts.forEach(d => { payload.append('departmentId[]', String(d)); payload.append('isAdmin[]', '1'); });
        if (avatar) payload.append('avatar', avatar);
        try {
            if (isEdit) {
                await axiosCaller('POST', `admin/users/${userId}?_method=PATCH`, payload);
                toast.success(t('admin.users.toast.updated'));
            } else {
                await axiosCaller('POST', 'admin/users', payload);
                toast.success(t('admin.users.toast.created'));
                history.push('/admin/all-users');
            }
        } catch (err: any) {
            // Centralised formatter surfaces 422 field errors ("password must be at least
            // 8 characters") and known business codes (company.user_limit_reached) instead
            // of the old uninformative generic toast. See utils/apiError.ts.
            toast.error(formatApiErrorMessage(err?.apiError, intl.formatMessage));
        }
        finally { setSaving(false); }
    };

    const handleDeactivate = async () => {
        try {
            await axiosCaller('DELETE', `admin/users/${userId}`);
            toast.success(t('admin.users.toast.deactivated'));
            history.push('/admin/all-users');
        } catch { toast.error(t('admin.toast.somethingWentWrong')); }
        finally { setDeactivateConfirm(false); }
    };

    const getInitials = () => ((firstname?.[0] || '') + (lastname?.[0] || '')).toUpperCase() || '?';

    if (loading) {
        return <div className="admin-page-container"><div className="admin-empty-state"><FormattedMessage id="common.loading" defaultMessage="Duke ngarkuar..." /></div></div>;
    }

    // ── Permission card renderer with icon ──
    const renderPermissionCard = (perm: Permission) => {
        const isSelected = selectedPermissions.includes(perm.value);
        const icon = MODULE_ICONS[perm.keyLabel];
        return (
            <button key={perm.value} className={`cuf-perm-card ${isSelected ? 'selected' : ''}`} onClick={() => toggleItem(selectedPermissions, setSelectedPermissions, perm.value)} type="button">
                <div className="cuf-perm-card-icon">{icon}</div>
                <span className="cuf-perm-card-label">{perm.label}</span>
                <span className="cuf-perm-card-check">
                    {isSelected ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                    ) : (
                        <span className="cuf-perm-card-unchecked" />
                    )}
                </span>
            </button>
        );
    };

    // ── Chip renderer for responsibilities/departments ──
    const renderChips = (items: { id: number; label: string }[], selected: number[], setSelected: (v: number[]) => void) => (
        <div className="cuf-chip-grid">
            {items.map(item => {
                const isSelected = selected.includes(item.id);
                return (
                    <button key={item.id} className={`cuf-chip ${isSelected ? 'selected' : ''}`} onClick={() => toggleItem(selected, setSelected, item.id)} type="button">
                        <span className="cuf-chip-check">
                            {isSelected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                        </span>
                        {item.label}
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className="admin-page-container">
            <button className="cuf-back-link" onClick={() => history.push('/admin/all-users')} type="button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                <FormattedMessage id="admin.users.backToList" defaultMessage="Të gjithë përdoruesit" />
            </button>

            {/* ── Profile Header ── */}
            <div className="cuf-profile-header">
                <div className="cuf-profile-avatar-section">
                    {avatarPreview
                        ? <img className="cuf-profile-avatar" src={avatarPreview} alt="" />
                        : <div className="cuf-profile-avatar-initials">{getInitials()}</div>}
                    <label className="cuf-profile-avatar-edit">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" /></svg>
                        <input type="file" accept="image/*" onChange={handleAvatarChange} hidden />
                    </label>
                </div>
                <div className="cuf-profile-info">
                    <h1 className="cuf-profile-name">
                        {firstname || lastname
                            ? `${firstname} ${lastname}`.trim()
                            : <FormattedMessage id="admin.users.newUser" defaultMessage="Përdorues i ri" />}
                    </h1>
                    {(isEdit || selectedPermissions.length > 0) && (
                        <div className="cuf-profile-meta">
                            <span className="cuf-profile-badge">
                                <FormattedMessage
                                    id="admin.users.permissionsCount"
                                    defaultMessage="{count, plural, one {# leje} other {# leje}}"
                                    values={{ count: selectedPermissions.length }}
                                />
                            </span>
                            {hasProductionPermission && selectedResponsibilities.length > 0 && (
                                <span className="cuf-profile-badge">
                                    <FormattedMessage
                                        id="admin.users.processesCount"
                                        defaultMessage="{count, plural, one {# proces} other {# procese}}"
                                        values={{ count: selectedResponsibilities.length }}
                                    />
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className="cuf-profile-save">
                    <button className="admin-btn-primary" onClick={handleSave} disabled={saving} type="button">
                        {saving
                            ? <FormattedMessage id="admin.users.saving" defaultMessage="Duke ruajtur..." />
                            : <FormattedMessage id="common.action.save" defaultMessage="Ruaj" />}
                    </button>
                </div>
            </div>

            {/* ── Tabs ── */}
            <div className="cuf-tabs">
                {visibleTabs.map(tab => (
                    <button key={tab.key} className={`cuf-tab ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)} type="button">
                        {tab.icon}<span><FormattedMessage id={tab.i18nKey} /></span>
                    </button>
                ))}
            </div>

            {/* ── Tab Content ── */}
            <div className="cuf-tab-content">

                {/* PROFILE TAB */}
                {activeTab === 'profile' && (
                    <>
                    <Card id="profile" title={<FormattedMessage id="admin.users.personalInfo" defaultMessage="Informacioni personal" />} collapsed={collapsed} setCollapsed={setCollapsed}>
                            <div className="cuf-grid-3">
                                <div className="cuf-field">
                                    <label><FormattedMessage id="admin.users.field.firstName" defaultMessage="Emri" /> *</label>
                                    <div className="cuf-input-icon-wrap">
                                        <svg className="cuf-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                        <input type="text" value={firstname} onChange={e => setFirstname(e.target.value)} />
                                    </div>
                                </div>
                                <div className="cuf-field">
                                    <label><FormattedMessage id="admin.users.field.lastName" defaultMessage="Mbiemri" /> *</label>
                                    <div className="cuf-input-icon-wrap">
                                        <svg className="cuf-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                        <input type="text" value={lastname} onChange={e => setLastname(e.target.value)} />
                                    </div>
                                </div>
                                <div className="cuf-field">
                                    <label><FormattedMessage id="admin.users.field.email" defaultMessage="Email" /></label>
                                    <div className="cuf-input-icon-wrap">
                                        <svg className="cuf-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                            <div className="cuf-grid-3" style={{ marginTop: 14 }}>
                                <div className="cuf-field">
                                    <label><FormattedMessage id="admin.users.field.username" defaultMessage="Username" /> *</label>
                                    <div className="cuf-input-icon-wrap">
                                        <span className="cuf-input-icon cuf-at-icon">@</span>
                                        <input type="text" value={username} onChange={e => setUsername(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                    </Card>

                    <Card id="password" title={<FormattedMessage id="admin.users.passwordSection" defaultMessage="Fjalëkalimi" />} collapsed={collapsed} setCollapsed={setCollapsed}>
                            <p className="cuf-card-desc">
                                {isEdit
                                    ? <FormattedMessage id="admin.users.passwordEditHint" defaultMessage="Lini bosh nëse nuk dëshironi të ndryshoni fjalëkalimin" />
                                    : <FormattedMessage id="admin.users.passwordHint" defaultMessage="Fjalëkalimi duhet të ketë së paku 8 karaktere" />}
                            </p>
                            <div className="cuf-grid-3">
                                <div className="cuf-field">
                                    <label><FormattedMessage id="admin.users.field.passwordNew" defaultMessage="Fjalëkalimi i ri" />{!isEdit && ' *'}</label>
                                    <div className="cuf-input-icon-wrap">
                                        <svg className="cuf-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                                        <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder={isEdit ? fm('admin.users.passwordPlaceholder', 'Lini bosh për ta mbajtur të njejtin') : ''} />
                                        <button type="button" className="cuf-eye-toggle" onClick={() => setShowPassword(v => !v)}>
                                            {showPassword ? (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19M1 1l22 22" /><path d="M14.12 14.12a3 3 0 11-4.24-4.24" /></svg>
                                            ) : (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className="cuf-field">
                                    <label><FormattedMessage id="admin.users.field.passwordConfirm" defaultMessage="Konfirmo fjalëkalimin" />{!isEdit && ' *'}</label>
                                    <div className="cuf-input-icon-wrap">
                                        <svg className="cuf-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                                        <input type={showPasswordConfirm ? 'text' : 'password'} value={passwordConfirmation} onChange={e => setPasswordConfirmation(e.target.value)} placeholder={isEdit ? fm('admin.users.passwordPlaceholder', 'Lini bosh për ta mbajtur të njejtin') : ''} />
                                        <button type="button" className="cuf-eye-toggle" onClick={() => setShowPasswordConfirm(v => !v)}>
                                            {showPasswordConfirm ? (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19M1 1l22 22" /><path d="M14.12 14.12a3 3 0 11-4.24-4.24" /></svg>
                                            ) : (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                    </Card>

                    {isEdit && Number(userId) !== currentUserId && (
                        <Card id="danger" title={<FormattedMessage id="admin.users.dangerZone" defaultMessage="Zona e rrezikshme" />} danger collapsed={collapsed} setCollapsed={setCollapsed}>
                                <div className="cuf-danger-row">
                                    <div>
                                        <p className="cuf-danger-title"><FormattedMessage id="admin.users.deactivateUser" defaultMessage="Deaktivizo përdoruesin" /></p>
                                        <p className="cuf-danger-desc"><FormattedMessage id="admin.users.deactivateDesc" defaultMessage="Ky veprim do të heqë aksesin e përdoruesit në sistem. Të gjitha të dhënat e tij do të ruhen." /></p>
                                    </div>
                                    <button className="cuf-danger-btn" onClick={() => setDeactivateConfirm(true)} type="button">
                                        <FormattedMessage id="admin.users.deactivate" defaultMessage="Deaktivizo" />
                                    </button>
                                </div>
                        </Card>
                    )}
                    </>
                )}

                {/* PERMISSIONS TAB */}
                {activeTab === 'permissions' && (
                    <>
                        {/* Permission matrix table */}
                        <Card id="permissions"
                            title={<FormattedMessage id="admin.users.permissions" defaultMessage="Lejet e aksesit" />}
                            hint={<FormattedMessage id="admin.users.selectedCount" defaultMessage="{count, plural, one {# e zgjedhur} other {# të zgjedhura}}" values={{ count: selectedPermissions.length }} />}
                            noPadding collapsed={collapsed} setCollapsed={setCollapsed}>
                            <div className="cuf-perm-table-wrap">
                                <table className="cuf-perm-table">
                                    <thead>
                                        <tr>
                                            <th className="cuf-perm-th-module"><FormattedMessage id="admin.users.module" defaultMessage="Moduli" /></th>
                                            {ACTION_COLUMNS.map(a => <th key={a.key} className="cuf-perm-th-action"><FormattedMessage id={a.i18nKey} /></th>)}
                                            <th className="cuf-perm-th-action"><FormattedMessage id="admin.users.all" defaultMessage="Të gjitha" /></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {PERMISSION_GROUPS.map(group => {
                                            const modulesInGroup = group.keys.filter(key => allPermissions.some(p => p.module === key));
                                            if (modulesInGroup.length === 0) return null;
                                            return (
                                                <React.Fragment key={group.i18nKey}>
                                                    <tr className="cuf-perm-group-row">
                                                        <td colSpan={ACTION_COLUMNS.length + 2}><FormattedMessage id={group.i18nKey} /></td>
                                                    </tr>
                                                    {modulesInGroup.map(moduleKey => {
                                                        const modulePerms = allPermissions.filter(p => p.module === moduleKey);
                                                        const icon = MODULE_ICONS[moduleKey];
                                                        const moduleLabelKey = MODULE_LABEL_KEYS[moduleKey];
                                                        const allSelected = modulePerms.every(p => selectedPermissions.includes(p.value));

                                                        const toggleAllModule = () => {
                                                            if (allSelected) {
                                                                setSelectedPermissions(prev => prev.filter(id => !modulePerms.some(p => p.value === id)));
                                                            } else {
                                                                setSelectedPermissions(prev => {
                                                                    const newIds = modulePerms.map(p => p.value).filter(id => !prev.includes(id));
                                                                    return [...prev, ...newIds];
                                                                });
                                                            }
                                                        };

                                                        return (
                                                            <tr key={moduleKey} className={`cuf-perm-module-row ${allSelected ? 'all-active' : ''}`}>
                                                                <td className="cuf-perm-td-module">
                                                                    <div className="cuf-perm-module-cell">
                                                                        <span className="cuf-perm-module-icon">{icon}</span>
                                                                        <span>{moduleLabelKey ? <FormattedMessage id={moduleLabelKey} /> : moduleKey}</span>
                                                                    </div>
                                                                </td>
                                                                {ACTION_COLUMNS.map(action => {
                                                                    const perm = modulePerms.find(p => p.action === action.key);
                                                                    if (!perm) return <td key={action.key} className="cuf-perm-td-action"><span className="cuf-perm-dash">{'—'}</span></td>;
                                                                    const isOn = selectedPermissions.includes(perm.value);
                                                                    return (
                                                                        <td key={action.key} className="cuf-perm-td-action">
                                                                            <span className={`cuf-toggle-switch ${isOn ? 'on' : ''}`} onClick={() => toggleItem(selectedPermissions, setSelectedPermissions, perm.value)}>
                                                                                <span className="cuf-toggle-knob" />
                                                                            </span>
                                                                        </td>
                                                                    );
                                                                })}
                                                                <td className="cuf-perm-td-action">
                                                                    <span className={`cuf-toggle-switch ${allSelected ? 'on' : ''}`} onClick={toggleAllModule}>
                                                                        <span className="cuf-toggle-knob" />
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Special permissions below table */}
                            {(() => {
                                const specialPerms = allPermissions.filter(p => !p.module);
                                if (specialPerms.length === 0) return null;
                                return (
                                    <div className="cuf-special-perms">
                                        {specialPerms.map(p => {
                                            const isOn = selectedPermissions.includes(p.value);
                                            const icon = MODULE_ICONS[p.keyLabel];
                                            return (
                                                <div key={p.value} className={`cuf-special-perm-row ${isOn ? 'active' : ''}`} onClick={() => toggleItem(selectedPermissions, setSelectedPermissions, p.value)}>
                                                    <span className="cuf-perm-module-icon">{icon}</span>
                                                    <span className="cuf-special-perm-label">
                                                        {p.keyLabel === 'fullPrivileges'
                                                            ? <FormattedMessage id="admin.users.fullPrivileges" defaultMessage="Administrator" />
                                                            : p.label}
                                                    </span>
                                                    <span className={`cuf-toggle-switch ${isOn ? 'on' : ''}`}>
                                                        <span className="cuf-toggle-knob" />
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </Card>

                        {hasProductionPermission && allResponsibilities.length > 0 && (
                            <Card id="responsibilities"
                                title={<FormattedMessage id="admin.users.responsibilities" defaultMessage="Proceset e prodhimit" />}
                                hint={<FormattedMessage id="admin.users.assignedCount" defaultMessage="{selected}/{total} caktuar" values={{ selected: selectedResponsibilities.length, total: allResponsibilities.length }} />}
                                collapsed={collapsed} setCollapsed={setCollapsed}>
                                    <div className="cuf-toggle-grid">
                                        {allResponsibilities.map(r => {
                                            const isOn = selectedResponsibilities.includes(r.id);
                                            return (
                                                <div key={r.id} className="cuf-toggle-grid-item" onClick={() => toggleItem(selectedResponsibilities, setSelectedResponsibilities, r.id)}>
                                                    <span className="cuf-toggle-grid-label">{r.label}</span>
                                                    <span className={`cuf-toggle-switch ${isOn ? 'on' : ''}`}>
                                                        <span className="cuf-toggle-knob" />
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                            </Card>
                        )}

                        {hasProductionPermission && allDepartments.length > 0 && (
                            <Card id="departments"
                                title={<FormattedMessage id="admin.users.departments" defaultMessage="Departamentet (Administrator)" />}
                                hint={<FormattedMessage id="admin.users.assignedCount" defaultMessage="{selected}/{total} caktuar" values={{ selected: selectedAdminDepts.length, total: allDepartments.length }} />}
                                collapsed={collapsed} setCollapsed={setCollapsed}>
                                    <p className="cuf-card-desc"><FormattedMessage id="admin.users.deptDesc" defaultMessage="Zgjidhni departamentet ku ky përdorues do të jetë administrator" /></p>
                                    <div className="cuf-toggle-grid">
                                        {allDepartments.map(d => {
                                            const isOn = selectedAdminDepts.includes(d.value);
                                            return (
                                                <div key={d.value} className="cuf-toggle-grid-item" onClick={() => toggleItem(selectedAdminDepts, setSelectedAdminDepts, d.value)}>
                                                    <span className="cuf-toggle-grid-label">{d.label}</span>
                                                    <span className={`cuf-toggle-switch ${isOn ? 'on' : ''}`}>
                                                        <span className="cuf-toggle-knob" />
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                            </Card>
                        )}
                    </>
                )}

                {/* ACTIVITY TAB */}
                {activeTab === 'activity' && isEdit && (
                    <Card id="activity" title={<FormattedMessage id="admin.users.activity" defaultMessage="Aktiviteti" />} collapsed={collapsed} setCollapsed={setCollapsed}>
                            <div className="cuf-activity-grid">
                                <div className="cuf-activity-item">
                                    <span className="cuf-activity-label"><FormattedMessage id="admin.users.accountCreated" defaultMessage="Llogaria e krijuar" /></span>
                                    <span className="cuf-activity-value">{createdAt || '—'}</span>
                                </div>
                                <div className="cuf-activity-item">
                                    <span className="cuf-activity-label"><FormattedMessage id="admin.users.permissionsLabel" defaultMessage="Leje aktive" /></span>
                                    <span className="cuf-activity-value">{selectedPermissions.length}</span>
                                </div>
                                <div className="cuf-activity-item">
                                    <span className="cuf-activity-label"><FormattedMessage id="admin.users.processesAssigned" defaultMessage="Procese të caktuara" /></span>
                                    <span className="cuf-activity-value">{selectedResponsibilities.length}</span>
                                </div>
                                <div className="cuf-activity-item">
                                    <span className="cuf-activity-label"><FormattedMessage id="admin.users.deptsLabel" defaultMessage="Departamente" /></span>
                                    <span className="cuf-activity-value">{selectedAdminDepts.length}</span>
                                </div>
                            </div>
                            <div className="cuf-activity-placeholder">
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c3d5e8" strokeWidth="1.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                                <p><FormattedMessage id="admin.users.activityComingSoon" defaultMessage="Historia e aktivitetit do të shtohet së shpejti" /></p>
                            </div>
                    </Card>
                )}

            </div>

            {/* Deactivate Confirmation */}
            <MikaConfirmModal
                isOpen={deactivateConfirm}
                onClose={() => setDeactivateConfirm(false)}
                onConfirm={handleDeactivate}
                title={fm('admin.users.deactivateTitle', 'Deaktivizo përdoruesin')}
                message={intl.formatMessage(
                    { id: 'admin.users.deactivateConfirm', defaultMessage: 'A jeni i sigurt që dëshironi të deaktivizoni {name}?' },
                    { name: `${firstname} ${lastname}` }
                )}
                confirmLabel={fm('admin.users.deactivate', 'Deaktivizo')}
                confirmColor="danger"
            />
        </div>
    );
};

export default withRouter(CreateUserForm as any);
